import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Lock, Apple } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { isNativeIOS, loadAppleSignIn } from "../lib/iap";
import { track } from "../lib/analytics";

function formatErr(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login: googleLogin, refresh, googleLoginAvailable } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [rememberMe, setRememberMe] = useState(false); // default OFF
  const [busy, setBusy] = useState(false);
  const native = isNativeIOS();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login"
          ? { email, password, remember_me: rememberMe }
          : { email, password, name };
      await http.post(path, body);
      await refresh();
      track(mode === "login" ? "user_signed_in" : "user_registered", { provider: "email" });
      toast.success(
        mode === "login"
          ? (lang === "en" ? "Welcome back!" : "Selamat kembali!")
          : (lang === "en" ? "Account created." : "Akaun dicipta.")
      );
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail, lang === "en" ? "Error. Please try again." : "Ralat. Sila cuba lagi."));
    } finally { setBusy(false); }
  };

  const appleSignIn = async () => {
    if (!native) { toast.info(lang === "en" ? "Sign in with Apple is available in the iOS App Store only." : "Sign in with Apple tersedia dalam iOS App Store sahaja."); return; }
    setBusy(true);
    try {
      const mod = await loadAppleSignIn();
      if (!mod?.SignInWithApple) throw new Error("Apple Sign In plugin unavailable");
      const res = await mod.SignInWithApple.authorize({
        clientId: "com.mlc.taip",
        redirectURI: window.location.origin + "/",
        scopes: "email name",
      });
      const idToken = res?.response?.identityToken;
      const nm = [res?.response?.givenName, res?.response?.familyName].filter(Boolean).join(" ");
      if (!idToken) throw new Error("Missing identity token");
      await http.post("/auth/apple", { id_token: idToken, name: nm });
      await refresh();
      track("user_signed_in", { provider: "apple" });
      toast.success(lang === "en" ? "Signed in with Apple" : "Log masuk dengan Apple berjaya");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(String(err.message || (lang === "en" ? "Apple Sign In failed" : "Apple Sign In gagal")));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E8E8ED]">
        <div className="max-w-md mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-[#F5F5F7]" data-testid="login-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#0A2540]" />
          </Link>
          <div className="font-semibold text-[#0A2540]">
            {mode === "login" ? t("login.title") : t("login.register_title")}
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col items-center mb-6">
          <img src="/taip-logo.png" alt="TAi-P" className="w-20 h-20 object-contain" />
          <div className="text-sm text-gray-500 mt-2">Plan Smarter. Travel Better.</div>
        </div>

        {/* Social login */}
       <div className="space-y-2 mb-5" style={{ display: native || googleLoginAvailable ? undefined : "none" }}>
          <button
            data-testid="apple-signin-btn"
            style={{ display: native ? undefined : "none" }}
            onClick={appleSignIn}
            disabled={busy}
            className="w-full py-3 rounded-full bg-black hover:bg-black/90 text-white font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Apple className="w-4 h-4" /> {t("login.apple")}
          </button>
          <button
            data-testid="google-signin-btn"
            style={{ display: googleLoginAvailable ? undefined : "none" }}
            onClick={googleLogin}
            disabled={busy}
            className="w-full py-3 rounded-full bg-white border border-[#E8E8ED] hover:bg-[#F5F5F7] text-[#1D1D1F] font-medium transition-colors"
          >
            {t("login.google")}
          </button>
        </div>

      <div className="flex items-center gap-3 text-xs text-gray-400 my-5" style={{ display: native || googleLoginAvailable ? undefined : "none" }}>
          <div className="flex-1 h-px bg-[#E8E8ED]" /> {t("login.or")} <div className="flex-1 h-px bg-[#E8E8ED]" />
        </div>

        {/* Email form — ORDER: Email → Password → Remember Me → Forgot Password? → Sign In */}
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <label className="text-xs text-gray-500">{t("login.name")}</label>
              <input
                data-testid="input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("login.name_ph")}
                className="w-full mt-1 px-4 py-3 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm"
              />
            </div>
          )}

          {/* 1. Email */}
          <div>
            <label className="text-xs text-gray-500">{t("login.email")}</label>
            <div className="relative mt-1">
              <Mail className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="input-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="anda@email.com"
                className="w-full pl-10 pr-4 py-3 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm"
              />
            </div>
          </div>

          {/* 2. Password */}
          <div>
            <label className="text-xs text-gray-500">{t("login.password")}</label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="input-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                placeholder={t("login.password_ph")}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm"
              />
            </div>
          </div>

          {/* 3. Remember Me + 4. Forgot Password? — login mode only */}
          {mode === "login" && (
            <div className="flex items-center justify-between pt-1 px-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  data-testid="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#0A2540] focus:ring-[#0A2540]/30"
                />
                <span className="text-xs text-gray-600">{t("login.remember_me")}</span>
              </label>
              <Link
                data-testid="forgot-password-link"
                to="/forgot-password"
                className="text-xs text-[#4FB3E8] font-medium hover:underline"
              >
                {t("login.forgot")}
              </Link>
            </div>
          )}

          {/* 5. Sign In button */}
          <button
            data-testid="email-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full py-3 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white font-medium transition-colors disabled:opacity-60"
          >
            {busy ? (lang === "en" ? "Please wait…" : "Sila tunggu…") : (mode === "login" ? t("login.submit_login") : t("login.submit_register"))}
          </button>
        </form>

        <div className="text-center text-sm text-gray-500 mt-5">
          {mode === "login" ? (
            <>{t("login.no_account")} <button data-testid="switch-register" onClick={() => setMode("register")} className="text-[#4FB3E8] font-medium">{t("login.register")}</button></>
          ) : (
            <>{t("login.has_account")} <button data-testid="switch-login" onClick={() => setMode("login")} className="text-[#4FB3E8] font-medium">{t("login.submit_login")}</button></>
          )}
        </div>

        <p className="text-[11px] text-gray-400 text-center mt-8 leading-relaxed">
          {t("login.consent")} <Link to="/terms" className="underline">{t("settings.terms")}</Link>, <Link to="/privacy" className="underline">{t("settings.privacy")}</Link>.
        </p>
      </main>
    </div>
  );
}
